import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Skeleton,
  IconButton,
} from "@mui/material";
import AttachmentIcon from "@mui/icons-material/Attachment";
import {
  CloudUpload as CloudUploadIcon,
  PlaylistAdd,
  Send as SendIcon,
} from "@mui/icons-material";
import { withAuth } from "../../hoc";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  GetAllSentFreeBiesRequest,
  GetSignUrlForFreeBiesPlanUpload,
  ISendFreeBiesRequest,
  SendFreeBie,
} from "../../services/FreebiesServices";
import DrawerSubscription from "./DrawerSubscription";
import { sendPutPdfRequest } from "../../services/DietplanServices";
import { useSnackbar } from "notistack";
import { getFreeBieRequestStatus } from "../../utils/commons";
import {
  GetSubscriptionById,
  ISubscription,
} from "../../services/SubscriptionServices";

const SentFreebie = () => {
  const [sentRequests, setSentRequests] = useState<ISendFreeBiesRequest[]>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestCount, setRequestCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const RequestPerPage = 15;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [subscriptionId, setSubscriptionId] = useState<string>("");
  const [pdf, setPdf] = useState<File | null>(null);
  const [triggerRemove, setTriggerRemove] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  useEffect(() => {
    fetchFreebries(currentPage || 1);
  }, [currentPage]);

  const resetFileds = () => {
    setPdf(null);
    setSubscriptionId("");
    setDescription("");
    setTitle("");
    setTriggerRemove(true);
  };

  const handleSendFreebie = async () => {
    // Handle send freebie logic
    setIsSending(true);

    if (!pdf || !title || !description || !subscriptionId) {
      return alert("All fileds required to sent freebries");
    }

    const signUrl = await GetSignUrlForFreeBiesPlanUpload({ name: pdf.name });
    const UploadPdf = await sendPutPdfRequest({ signUrl, file: pdf });
    if (UploadPdf.status !== 200) {
      enqueueSnackbar("Failed to upload PDF", { variant: "error" });
      return;
    }

    const status = await SendFreeBie({
      title,
      description,
      attachmentUrl: signUrl.split("?")[0],
      subscriptionId,
    });
    setIsSending(false);
    if (status == 201) {
      enqueueSnackbar("Request Sent Successfully!", {
        variant: "success",
      });

      reloadList();
      resetFileds();
    } else {
      enqueueSnackbar("Failed to Sent Request. Please try again later");
    }
  };

  const fetchFreebries = async (page: number) => {
    setLoading(true);
    setCurrentPage(page);
    const data = await GetAllSentFreeBiesRequest({
      page,
      size: RequestPerPage,
    });
    setSentRequests(data.content);
    setRequestCount(data.totalElements);
    setTotalPages(data.totalPages);
    setLoading(false);
  };

  const handleFileUpload = (selectedFile: File, url: string) => {
    setPdf(selectedFile);
  };
  const handleFileRemove = () => {
    setPdf(null);
  };

  const reloadList = async () => {
    await fetchFreebries(1);
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };
  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  const handleRemoveProgram = () => {
    setSubscriptionId("");
  };
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        padding: 2,
        height: "80vh",
      }}
    >
      <DrawerSubscription
        open={openDrawer}
        onClose={handleDrawerClose}
        onUpdate={reloadList}
        setSelected={setSubscriptionId}
      />
      <Box sx={{ width: "70%" }}>
        <FreeBiesTable
          isLoading={loading as boolean}
          ArrayRequests={sentRequests as ISendFreeBiesRequest[]}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          requestCount={requestCount}
        />
      </Box>
      <Box
        sx={{ width: "30%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography variant="h5">Send Freebie</Typography>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <TextField
          label="Description"
          multiline
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <FileUploadBox
          onFileUpload={handleFileUpload}
          onFileRemove={handleFileRemove}
          triggerRemove={triggerRemove}
        />

        <UploadFreeBie
          handleClick={handleDrawerOpen}
          SubscriptionsId={subscriptionId}
          handleRemove={handleRemoveProgram}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          disabled={isSending}
          onClick={handleSendFreebie}
        >
          Send Freebie
        </Button>
      </Box>
    </Box>
  );
};

export default withAuth(SentFreebie);

interface FileUploadBoxProps {
  onFileUpload: (file: File, url: string) => void;
  onFileRemove: () => void;
  triggerRemove: boolean;
}

const FileUploadBox: React.FC<FileUploadBoxProps> = ({
  onFileUpload,
  onFileRemove,
  triggerRemove,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  useEffect(() => {
    handleRemove();
  }, [triggerRemove]);

  const handleUpload = (event: any) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile) {
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      if (onFileUpload) {
        onFileUpload(selectedFile, url);
      }
    }
  };

  const handleRemove = () => {
    setFile(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (onFileRemove) {
      onFileRemove();
    }
  };

  return (
    <Box
      sx={{
        border: `2px dashed ${
          file ? theme.palette.success.main : theme.palette.divider
        }`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(5),
        textAlign: "center",
        position: "relative",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
          cursor: "pointer",
        },
      }}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        accept=".pdf"
        style={{ display: "none" }}
        disabled={file != null}
        onChange={handleUpload}
      />
      <label
        htmlFor="upload-file"
        style={{ display: "block", cursor: "pointer" }}
      >
        {!file && (
          <>
            <UploadFileIcon color="action" />
            <Typography variant="body2" color="textSecondary">
              Upload PDF
            </Typography>
          </>
        )}
      </label>
      {file && (
        <>
          <UploadFileIcon color="action" />
          <Typography variant="body2" color="textSecondary">
            {file.name}
          </Typography>
          {previewUrl && (
            <Typography variant="body2" color="textSecondary">
              Preview:{" "}
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                View PDF
              </a>
            </Typography>
          )}
          <IconButton
            sx={{
              position: "absolute",
              top: theme.spacing(1),
              right: theme.spacing(1),
              backgroundColor: theme.palette.background.paper,
            }}
            onClick={handleRemove}
          >
            <DeleteIcon color="error" />
          </IconButton>
        </>
      )}
    </Box>
  );
};

const FreeBiesTable = ({
  isLoading,
  ArrayRequests,
  currentPage,
  setCurrentPage,
  totalPages,
  requestCount,
}: {
  isLoading: boolean;
  ArrayRequests: ISendFreeBiesRequest[];
  currentPage: number;
  totalPages: number;
  requestCount: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const theme = useTheme();

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <>
      <TableHeaderComponent />
      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          scrollbarWidth: "none",
        }}
      >
        <TableContainer>
          <Table>
            <TableBody>
              {isLoading && (
                <>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <LoadingSkeletonRow key={index} />
                  ))}
                </>
              )}

              {!isLoading && ArrayRequests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && ArrayRequests.length > 0 && (
                <>
                  {ArrayRequests.map((request) => (
                    <TableRow
                      onClick={() => {
                        // setSelectedRequest(plan);
                        // handleOpen();
                      }}
                      key={request.id}
                    >
                      <TableCell
                        sx={{ width: 200, maxWidth: 200, minWidth: 200 }}
                        align="left"
                      >
                        {request.title}
                        <br />
                        {request.description}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: 100,
                          maxWidth: 100,
                          minWidth: 100,
                        }}
                        align="center"
                      >
                        <span
                          onClick={() =>
                            window.open(request.attachmentUrl, "_blank")
                          }
                        >
                          {" "}
                          <AttachmentIcon sx={{ cursor: "pointer" }} />
                        </span>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                      >
                        {request.subscriptionId}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                      >
                        {getFreeBieRequestStatus(request.status as string)}
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "auto",
          marginBottom: 0,
          color: theme.palette.text.secondary,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" sx={{ marginRight: 1 }}>
            Sent Count: <b>{requestCount}</b>&nbsp;,
          </Typography>
          <Typography variant="body2">
            Page <b>{currentPage}</b> of {totalPages}
          </Typography>
        </Box>

        <Box>
          <Button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </Box>
      </Box>
    </>
  );
};

const TableHeaderComponent = () => {
  const theme = useTheme();
  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.info.light,
              }}
            >
              <TableCell
                sx={{ width: 200, maxWidth: 200, minWidth: 200 }}
                align="center"
              >
                title/description
              </TableCell>
              <TableCell
                sx={{ width: 100, maxWidth: 100, minWidth: 100 }}
                align="center"
              >
                Attachments
              </TableCell>
              <TableCell
                sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                align="center"
              >
                Program
              </TableCell>
              <TableCell
                sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
                align="center"
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>
    </>
  );
};

const LoadingSkeletonRow = () => {
  const theme = useTheme();

  return (
    <TableRow
      sx={{
        cursor: "pointer",
      }}
    >
      <TableCell sx={{ width: 200, maxWidth: 200, minWidth: 200 }} align="left">
        <Skeleton variant="text" width={150} />
      </TableCell>
      <TableCell
        sx={{ width: 100, maxWidth: 100, minWidth: 100 }}
        align="center"
      >
        <Skeleton variant="text" width={100} />
      </TableCell>
      <TableCell
        align="center"
        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
      >
        <Skeleton variant="text" width={130} />
      </TableCell>
      <TableCell
        align="center"
        sx={{ width: 130, maxWidth: 130, minWidth: 130 }}
      >
        <Skeleton variant="text" width={130} />
      </TableCell>
    </TableRow>
  );
};

const UploadFreeBie = ({
  SubscriptionsId,
  handleClick,
  handleRemove,
}: {
  SubscriptionsId: string;
  handleClick: () => void;
  handleRemove: () => void;
}) => {
  const [Subscriptions, setSubscriptions] = useState<ISubscription | null>(
    null
  );

  useEffect(() => {
    if (SubscriptionsId) {
      fetchSubsciption();
    } else {
      setSubscriptions(null);
    }
  }, [SubscriptionsId]);

  const fetchSubsciption = async () => {
    const fetchData = await GetSubscriptionById({ id: SubscriptionsId });
    setSubscriptions(fetchData);
  };
  return (
    <>
      <Box width={"100%"} flex={1} marginRight={2}>
        <Box
          sx={{
            position: "relative",
            width: "60%",
            aspectRatio: "16 / 9",
            backgroundColor: Subscriptions ? "transparent" : "gray",
            border: "1.5px solid grey[400]",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {Subscriptions ? (
            <>
              <Box
                component="img"
                src={Subscriptions.thumbnailUrl as string}
                alt={`Subscription`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </>
          ) : (
            <Button variant="contained" component="label" onClick={handleClick}>
              <PlaylistAdd />
            </Button>
          )}
          {Subscriptions && (
            <Button
              variant="contained"
              component="label"
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
              }}
              onClick={handleRemove}
            >
              Remove
            </Button>
          )}
        </Box>

        {/* {freebie && ( */}
        <Box display={"flex"} marginTop={2}>
          {/* <Box
                      component="img"
                      src={freebie.playList.thumbnailUrl}
                      alt={`Video ${index + 1}`}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: "cover",
                        borderRadius: "50%",
                        marginRight: 2,
                      }}
                    /> */}
          <Typography variant="body2" color={"gray"}>
            <b>{Subscriptions?.name}</b> <br />
            {/* {freebie.playList.description.slice(0, 50)} */}
            {/* <b>{freebie.playList.videos.length}</b> videos */}
          </Typography>
        </Box>
        {/* )} */}
      </Box>
    </>
  );
};
